import { Box, HBox, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import Input from "@ui/input/Input"
import { Typo } from "@ui/text/Typo"
import { openNestedUniversalDialog, S_UNIVERSAL_DIALOG } from "./universalDialog/signals"
import { FormParser } from "src/view/FormParser"
import { GD } from "src/GD"
import { getIssuerDisplayName } from "../issuer/issuerIdentity"
import { FC, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { Avatar, ControlButton, DS, DetailRow, EntityHeader, QuickAction, QuickActions, Section, SettingRow, Stage, StageButton, StageStat, UnderlineTabs, FormIntro, Glyph } from "../ds/Kit"
import { StatusBadge } from "./StatusBadge"
import { toast } from "react-toastify"
import { amountValidationError, parsePositiveAmountToMinorUnits } from "src/utils/amounts"
import { CardsList } from "./CardsList"
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter"
import { USER_ROLE } from "src/services/AuthService"
import { CardDetails } from "./CardDetails"
import { TransactionDetails } from "./TransactionDetails"
import { DEFAULT_CURRENCY } from "src/constants"
import QRCode from "qrcode"
import { Spinner } from "@ui/spinner/Spinner"
import { IconCopy } from "@ui/icons/IconCopy"
import { KycSection } from "./KycSection"
import { getFriendlyErrorMessage } from "src/utils/errors"
import {
	getUserBlockProgressMessage,
	summarizeBuyerCards,
	summarizeUserBlockProgress,
	type UserBlockProgressSummary
} from "src/utils/userCardStatus"
import { useOptionalIssuer } from "../issuer/IssuerProvider"
import { isRunningInNativeApp, openGoogleAuthenticator } from "src/utils/nativePlatform"
import { Error as FormError } from "@ui/error/Error"
import { UserBlockReturnPreview } from "./UserBlockReturnPreview"

interface UserDetailsProps {
	userUid: string
	onClose: () => void
	onUpdate?: () => void | Promise<void>
	teamUid?: string
}

const TWO_FACTOR_CODE_LENGTH = 6
const USER_BLOCK_POLL_INTERVAL_MS = 10000
const USER_BLOCK_MAX_POLL_ATTEMPTS = 12
const USER_BLOCK_MAX_STATUS_ERRORS = 3
const PERCENT_SCALE = 100
const USER_DETAILS_BRAND = "#2B5C8A"
const USER_DETAILS_TEXT = "#17243B"
const USER_DETAILS_MUTED = "#6B7280"
const USER_DETAILS_BORDER = "#E3E7EF"
const USER_DETAILS_SOFT = "#F8FAFC"
const USER_DETAILS_SHADOW = "0 1px 2px rgba(16,24,40,0.04), 0 4px 12px rgba(16,24,40,0.06)"
const RETURN_BALANCE_PRESET_SMALL = 100
const RETURN_BALANCE_PRESET_MEDIUM = 500
const RETURN_BALANCE_PRESET_LARGE = 1000
const RETURN_BALANCE_PRESETS = [
	RETURN_BALANCE_PRESET_SMALL,
	RETURN_BALANCE_PRESET_MEDIUM,
	RETURN_BALANCE_PRESET_LARGE
] as const
const CURRENCY_MINOR_UNITS = 100
const CURRENCY_DECIMAL_PLACES = 2

const withReturnedBuyerBalance = <T extends {
	team_balance?: number
	team_balances?: IUserIssuerBalance[]
}>(
	current: T,
	balance: number,
	issuer: number | null,
	currency: string
): T => ({
	...current,
	team_balance: balance,
	...(issuer === null
		? {}
		: {
			team_balances: [
				...(current.team_balances ?? []).filter(account => account.issuer !== issuer),
				{ issuer, currency, team_balance: balance }
			]
		})
})

type UserActionPrompt = "block" | "unblock" | null
type UserOperationPhase = "pending" | "success" | "warning" | "error"

interface UserOperationNoticeState {
	phase: UserOperationPhase
	title: string
	message: string
	progress?: UserBlockProgressSummary
}

interface ReturnBalanceFormProps {
	availableBalance: number
	currency: string
	onCancel: () => void
	onSubmit: (amount: number) => Promise<{ error?: unknown }>
}

const ReturnBalanceForm: FC<ReturnBalanceFormProps> = ({
	availableBalance,
	currency,
	onCancel,
	onSubmit
}) => {
	const [amount, setAmount] = useState("")
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<unknown>(null)
	const maxAmount = (availableBalance / CURRENCY_MINOR_UNITS).toFixed(CURRENCY_DECIMAL_PLACES)
	const formattedBalance = formatCurrencyWithNumberToSymbolWithNumber(
		availableBalance,
		currency
	)

	const submit = async () => {
		if (busy) return
		setError(null)
		const parsedAmount = parsePositiveAmountToMinorUnits(amount)
		if (typeof parsedAmount.amount !== "number") {
			setError({ code: 0, message: parsedAmount.error || "Enter a valid amount." })
			return
		}
		if (parsedAmount.amount > availableBalance) {
			setError({
				code: 0,
				message: `Amount cannot exceed the available balance of ${formattedBalance}.`
			})
			return
		}

		setBusy(true)
		try {
			const response = await onSubmit(parsedAmount.amount)
			if (response.error) setError(response.error)
		} catch (cause) {
			setError(cause)
		} finally {
			setBusy(false)
		}
	}

	return (
		<VBox data-dialog-layout='amount-form' gap='16px'>
			<Box
				role='region'
				aria-label='Available balance'
				sx={{
					padding: "14px 16px",
					borderRadius: "12px",
					backgroundColor: USER_DETAILS_SOFT,
					border: `1px solid ${USER_DETAILS_BORDER}`
				}}
			>
				<Typo variant='caption' sx={{ color: USER_DETAILS_MUTED, fontWeight: 700 }}>
					Available balance
				</Typo>
				<Typo sx={{ color: USER_DETAILS_TEXT, fontSize: "1.2rem", fontWeight: 750 }}>
					{formattedBalance}
				</Typo>
			</Box>

			<VBox gap='10px'>
				<HBox gap='10px' sx={{ alignItems: "flex-end" }}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Input
							label='Amount'
							name='amount'
							data={{ amount }}
							onChange={(value: string) => {
								setAmount(String(value ?? ""))
								setError(null)
							}}
							placeholder='0.00'
							autoComplete='off'
							type='number'
							disabled={busy}
						/>
					</Box>
					<Button
						title='MAX'
						variant='secondary'
						disabled={busy || availableBalance <= 0}
						onClick={() => {
							setAmount(maxAmount)
							setError(null)
						}}
						sx={{ minWidth: "82px", height: "44px" }}
					/>
				</HBox>
				<HBox gap='8px' sx={{ flexWrap: "wrap" }}>
					{RETURN_BALANCE_PRESETS.map((preset) => (
						<Button
							key={preset}
							title={String(preset)}
							variant='secondarySmall'
							disabled={busy || preset * CURRENCY_MINOR_UNITS > availableBalance}
							onClick={() => {
								setAmount(String(preset))
								setError(null)
							}}
							sx={{ minWidth: "72px" }}
						/>
					))}
				</HBox>
			</VBox>

			{Boolean(error) && <FormError error={error} />}

			<HBox
				gap='10px'
				sx={{
					justifyContent: "flex-end",
					flexWrap: "wrap",
					paddingTop: "14px",
					borderTop: "1px solid #E8EEF6"
				}}
			>
				<Button title='Cancel' variant='secondary' disabled={busy} onClick={onCancel} />
				<Button
					title='Return Balance'
					variant='primary'
					disabled={busy || availableBalance <= 0}
					onClick={submit}
				/>
			</HBox>
		</VBox>
	)
}

interface CardIssueLimitFormData {
	card_issue_limit: number | string
	card_issue_limit_status: "active" | "disabled"
}

type UserBlockMonitorResult =
	| { kind: "completed"; data: IUserBlockStatus }
	| { kind: "failed"; data: IUserBlockStatus }
	| { kind: "status-error"; error: unknown }
	| { kind: "timeout" }

const waitForUserBlockPoll = () =>
	new Promise<void>((resolve) => window.setTimeout(resolve, USER_BLOCK_POLL_INTERVAL_MS))

const monitorUserBlockOperation = async (
	operationUid: string,
	onProgress: (status: IUserBlockStatus) => void,
	onRetry: (error: unknown, attempt: number) => void
): Promise<UserBlockMonitorResult> => {
	let consecutiveStatusErrors = 0

	for (let attempt = 0; attempt < USER_BLOCK_MAX_POLL_ATTEMPTS; attempt += 1) {
		const response = await GD.REQ_USER_BLOCK_STATUS.request({ operation_uid: operationUid })

		if (response.error) {
			consecutiveStatusErrors += 1
			onRetry(response.error, consecutiveStatusErrors)
			if (consecutiveStatusErrors >= USER_BLOCK_MAX_STATUS_ERRORS) {
				return { kind: "status-error", error: response.error }
			}
			await waitForUserBlockPoll()
			continue
		}

		consecutiveStatusErrors = 0
		onProgress(response.data)
		if (response.data.status === "completed") return { kind: "completed", data: response.data }
		if (response.data.status === "failed") return { kind: "failed", data: response.data }

		await waitForUserBlockPoll()
	}

	return { kind: "timeout" }
}

const completeTrackedToast = (
	toastId: string,
	message: string,
	type: "success" | "warning" | "error"
) => {
	if (toast.isActive(toastId)) {
		toast.update(toastId, {
			render: message,
			type,
			autoClose: 6000,
			closeOnClick: true
		})
		return
	}

	if (type === "success") toast.success(message)
	else if (type === "warning") toast.warn(message)
	else toast.error(message)
}

interface ITransactionWithCard extends ICardTransaction {
	card?: ICardSaved
	companyTransaction?: ICompanyTrxTransaction
}

const SectionLabel: FC<{ children: ReactNode }> = ({ children }) => (
	<Typo
		variant='caption'
		sx={{
			color: USER_DETAILS_TEXT,
			fontSize: "0.9rem",
			fontWeight: 700,
			marginBottom: "4px"
		}}
	>
		{children}
	</Typo>
)

const isPrimitiveValue = (value: ReactNode): value is string | number =>
	typeof value === "string" || typeof value === "number"

const BreakableEmail: FC<{ email: string }> = ({ email }) => (
	<>
		{email.split(/([@._+-])/).map((part, index) => (
			<Fragment key={`${part}-${index}`}>
				{part}
				{/[@._+-]/.test(part) ? <wbr /> : null}
			</Fragment>
		))}
	</>
)

const FieldRow: FC<{
	label: string
	value?: ReactNode
	mono?: boolean
	onCopy?: () => void
}> = ({ label, value, mono, onCopy }) => (
	<HBox
		sx={{
			alignItems: "center",
			justifyContent: "space-between",
			padding: "10px 0",
			borderBottom: `1px solid ${USER_DETAILS_SOFT}`
		}}
	>
		<Typo
			variant='caption'
			sx={{
				color: USER_DETAILS_MUTED,
				fontSize: "0.875rem",
				flex: "0 0 128px"
			}}
		>
			{label}
		</Typo>
		<HBox
			gap='8px'
			sx={{ alignItems: "center", flex: 1, minWidth: 0, justifyContent: "flex-end" }}
		>
			{isPrimitiveValue(value) || value === undefined || value === null ? (
				<Typo
					variant='medium'
					sx={{
						fontWeight: 500,
						color: USER_DETAILS_TEXT,
						fontSize: "0.875rem",
						textAlign: "right",
						fontFamily: mono ? "ui-monospace, monospace" : undefined,
						userSelect: "text",
						minWidth: 0,
						overflowWrap: "anywhere"
					}}
				>
					{value ?? "—"}
				</Typo>
			) : (
				value
			)}
			{onCopy && (
				<Box
					sx={{
						cursor: "pointer",
						color: "#9CA3AF",
						"&:hover": { color: USER_DETAILS_BRAND },
						"& svg": { width: "14px", height: "14px" }
					}}
					onClick={onCopy}
				>
					<IconCopy />
				</Box>
			)}
		</HBox>
	</HBox>
)


const operationNoticeColors: Record<
	UserOperationPhase,
	{ background: string; border: string; title: string; progress: string }
> = {
	pending: {
		background: "#EFF6FF",
		border: "#93C5FD",
		title: "#1D4ED8",
		progress: "#2563EB"
	},
	success: {
		background: "#ECFDF5",
		border: "#86EFAC",
		title: "#047857",
		progress: "#10B981"
	},
	warning: {
		background: "#FFFBEB",
		border: "#FCD34D",
		title: "#B45309",
		progress: "#F59E0B"
	},
	error: {
		background: "#FEF2F2",
		border: "#FCA5A5",
		title: "#B91C1C",
		progress: "#EF4444"
	}
}

const UserOperationNotice: FC<{
	operation: UserOperationNoticeState
	onDismiss: () => void
	onRefresh: () => void
}> = ({ operation, onDismiss, onRefresh }) => {
	const colors = operationNoticeColors[operation.phase]
	const progressPercent = operation.progress?.total
		? Math.round((operation.progress.processed / operation.progress.total) * PERCENT_SCALE)
		: 0
	const isPending = operation.phase === "pending"

	return (
		<VBox
			gap='10px'
			role='status'
			aria-live={isPending ? "polite" : "assertive"}
			sx={{
				backgroundColor: colors.background,
				border: `1px solid ${colors.border}`,
				borderRadius: "12px",
				padding: "14px 16px"
			}}
		>
			<HBox sx={{ alignItems: "flex-start", justifyContent: "space-between", gap: "14px" }}>
				<VBox gap='4px' sx={{ minWidth: 0 }}>
					<Typo sx={{ color: colors.title, fontWeight: 800, fontSize: "0.9rem" }}>
						{operation.title}
					</Typo>
					<Typo sx={{ color: USER_DETAILS_TEXT, fontSize: "0.84rem", lineHeight: 1.5 }}>
						{operation.message}
					</Typo>
				</VBox>
				{!isPending && (
					<HBox gap='8px' sx={{ flexShrink: 0 }}>
						{operation.phase !== "success" && (
							<Button
								title='Refresh'
								variant='secondarySmall'
								onClick={onRefresh}
							/>
						)}
						<Button
							title='Dismiss'
							variant='secondarySmall'
							onClick={onDismiss}
						/>
					</HBox>
				)}
			</HBox>
			{operation.progress && operation.progress.total > 0 && (
				<>
					<Box
						sx={{
							height: "6px",
							width: "100%",
							borderRadius: "999px",
							backgroundColor: "rgba(148, 163, 184, 0.24)",
							overflow: "hidden"
						}}
					>
						<Box
							sx={{
								height: "100%",
								width: `${progressPercent}%`,
								backgroundColor: colors.progress,
								transition: "width 0.25s ease"
							}}
						/>
					</Box>
					<Typo sx={{ color: USER_DETAILS_MUTED, fontSize: "0.75rem" }}>
						Completed: {operation.progress.completed} · Pending: {operation.progress.pending} ·
						 Failed: {operation.progress.failed} · Skipped: {operation.progress.skipped}
					</Typo>
				</>
			)}
		</VBox>
	)
}

const UserActionPromptPanel: FC<{
	kind: Exclude<UserActionPrompt, null>
	busy: boolean
	returnPreview: ReactNode
	returnPreviewLoading: boolean
	frozenCardCount: number
	onFreeze: () => void
	onBlock: () => void
	onRestoreLogin: () => void
	onRestoreCards: () => void
	onCancel: () => void
}> = ({
	kind,
	busy,
	returnPreview,
	returnPreviewLoading,
	frozenCardCount,
	onFreeze,
	onBlock,
	onRestoreLogin,
	onRestoreCards,
	onCancel
}) => (
	<VBox
		gap='12px'
		sx={{
			backgroundColor: "#FFFFFF",
			border: `1px solid ${USER_DETAILS_BORDER}`,
			borderRadius: "12px",
			padding: "16px",
			boxShadow: USER_DETAILS_SHADOW
		}}
	>
		<Typo sx={{ color: USER_DETAILS_TEXT, fontWeight: 800, fontSize: "0.9rem" }}>
			{kind === "block" ? "Choose how to block this user" : "Choose how to restore this user"}
		</Typo>
		<Typo sx={{ color: USER_DETAILS_MUTED, fontSize: "0.84rem", lineHeight: 1.5 }}>
			{kind === "block"
				? "Freeze is reversible and does not move money. Closing provider cards is irreversible: card balances and the buyer’s personal balance for every issuer are returned to the company."
				: frozenCardCount > 0
					? `Restore login only, or restore login and unfreeze ${frozenCardCount} currently frozen ${frozenCardCount === 1 ? "card" : "cards"}.`
					: "There are no frozen cards to restore. Closed or blocked cards cannot be reopened."}
		</Typo>
		{kind === "block" && returnPreview}
		<HBox gap='10px' sx={{ flexWrap: "wrap" }}>
			{kind === "block" ? (
				<>
					<Button
						title='Block User + Freeze Cards'
						variant='secondary'
						disabled={busy}
						onClick={onFreeze}
					/>
					<Button
						title='Block User + Close Cards'
						variant='secondary'
						disabled={busy || returnPreviewLoading}
						onClick={onBlock}
						sx={{ color: "#B91C1C", borderColor: "#FCA5A5" }}
					/>
				</>
			) : (
				<>
					<Button
						title='Restore Login Only'
						variant='secondary'
						disabled={busy}
						onClick={onRestoreLogin}
					/>
					{frozenCardCount > 0 && (
						<Button
							title={`Restore Login + Unfreeze ${frozenCardCount} ${frozenCardCount === 1 ? "Card" : "Cards"}`}
							variant='secondary'
							disabled={busy}
							onClick={onRestoreCards}
						/>
					)}
				</>
			)}
			<Button title='Cancel' variant='secondary' disabled={busy} onClick={onCancel} />
		</HBox>
	</VBox>
)

export const UserDetails = ({
	userUid,
	onClose,
	onUpdate,
	teamUid
}: UserDetailsProps) => {
	const nativeApp = isRunningInNativeApp()
	const [user, setUser] = useState<IUserSaved | null>(null)
	const [profile] = GD.REQ_PROFILE.useRequest()
	const issuerContext = useOptionalIssuer()
	const selectedIssuerId = issuerContext?.selectedIssuerId ?? null
	const issuerCurrency = issuerContext?.selectedIssuer?.currency || DEFAULT_CURRENCY
	const company = GD.S_COMPANY_UPDATED.use()
	const [activeTab, setActiveTab] = useState<"overview" | "cards" | "transactions">(
		"overview"
	)
	const [cards, setCards] = useState<ICardSaved[]>([])
	const [cardsLoaded, setCardsLoaded] = useState(false)
	const buyerCardSummary = useMemo(() => summarizeBuyerCards(cards), [cards])
	const actualCardsBalance = useMemo(
		() => cards.reduce((total, card) => total + Number(card.balance || 0), 0),
		[cards]
	)
	const [transactions, setTransactions] = useState<ITransactionWithCard[]>([])
	const [isLoadingTransactions, setIsLoadingTransactions] = useState(false)
	const [twoFactorSetupUrl, setTwoFactorSetupUrl] = useState("")
	const [twoFactorQrCodeDataUrl, setTwoFactorQrCodeDataUrl] = useState("")
	const [twoFactorCode, setTwoFactorCode] = useState("")
	const [disableTwoFactorPassword, setDisableTwoFactorPassword] = useState("")
	const [showDisableTwoFactorForm, setShowDisableTwoFactorForm] = useState(false)
	const [twoFactorBusy, setTwoFactorBusy] = useState(false)
	const [userActionBusy, setUserActionBusy] = useState(false)
	const [returnPreviewLoading, setReturnPreviewLoading] = useState(true)
	const [userActionPrompt, setUserActionPrompt] = useState<UserActionPrompt>(null)
	const [userOperation, setUserOperation] = useState<UserOperationNoticeState | null>(null)
	const userDetailsDisposed = useRef(false)
	const activeUserUid = useRef(userUid)
	const activeIssuerId = useRef(selectedIssuerId)
	const [twoFactorError, setTwoFactorError] = useState<string | null>(null)

	useEffect(() => {
		userDetailsDisposed.current = false
		return () => {
			userDetailsDisposed.current = true
		}
	}, [])

	useEffect(() => {
		activeUserUid.current = userUid
	}, [userUid])

	useEffect(() => {
		activeIssuerId.current = selectedIssuerId
		setCards([])
		setCardsLoaded(false)
	}, [selectedIssuerId])
	const [twoFactorMethodOverride, setTwoFactorMethodOverride] = useState<
		TTwoFactorMethod | null | undefined
	>(undefined)
	const [userPhone, setUserPhone] = useState<string | null>(null)
	const [userPhoneLoading, setUserPhoneLoading] = useState(false)

	useEffect(() => {
		let active = true
		const load = async () => {
			setUser(null)
			const userResponse = await GD.REQ_GET_USER.request(userUid)
			if (!active) return
			if (userResponse?.data) {
				setUser(userResponse.data)
				return
			}
			// `users/get` is not permitted for every account (e.g. private companies
			// only allow `users/get/me`). When viewing your own profile, fall back to
			// the cached session profile so the dialog still loads instead of spinning.
			const me = await GD.REQ_PROFILE.request()
			if (active && me && me.uid === userUid) setUser(me as unknown as IUserSaved)
		}
		if (userUid) load()
		return () => {
			active = false
		}
	}, [selectedIssuerId, userUid])

	useEffect(() => {
		setTwoFactorSetupUrl("")
		setTwoFactorQrCodeDataUrl("")
		setTwoFactorCode("")
		setDisableTwoFactorPassword("")
		setShowDisableTwoFactorForm(false)
		setTwoFactorBusy(false)
		setTwoFactorError(null)
		setTwoFactorMethodOverride(undefined)
		setActiveTab("overview")
		setUserActionPrompt(null)
		setUserOperation(null)
		setUserActionBusy(false)
	}, [user?.uid])

	const loadData = async () => {
		const targetUserUid = user?.uid
		if (!targetUserUid) return
		const requestIssuerId = selectedIssuerId
		const cardsRes = await GD.REQ_CARDS.request()
		if (
			userDetailsDisposed.current ||
			activeUserUid.current !== targetUserUid ||
			activeIssuerId.current !== requestIssuerId
		) return
		if (!cardsRes.data) {
			setCardsLoaded(false)
			return
		}
		const userCards = (cardsRes.data as ICardSaved[]).filter(
			(c) => c.user_uid === targetUserUid
		)
		setCards(userCards)
		setCardsLoaded(true)

		if (activeTab === "transactions") {
			setIsLoadingTransactions(true)
			if (user?.uid && user.company_role === USER_ROLE.BUYER) {
				const res = await GD.REQ_COMPANY_TRANSACTIONS_ALL.request({
					buyer_uid: user.uid,
					page: 0,
					limit: 100
				})
				if (res.data) {
					setTransactions(
						res.data.transactions.map((t) => ({
							id: String(t.id),
							amount: t.amount,
							currency: t.currency,
							date: t.created_at,
							description: t.description || "Transaction",
							status: t.status,
							direction: t.direction,
							created_at: t.created_at,
							merchant: t.merchant,
							companyTransaction: t
						}))
					)
				}
			} else {
				const all: ITransactionWithCard[] = []
				await Promise.all(
					userCards.map(async (card) => {
						const r = await GD.REQ_CARD_TRANSACTIONS.request({
							uid: card.uid,
							card_holder_uid: card.card_holder_uid
						})
						if (r.data) r.data.forEach((tx) => all.push({ ...tx, card }))
					})
				)
				setTransactions(all)
			}
			setIsLoadingTransactions(false)
		}
	}

	const openTransactionDetails = (row: ITransactionWithCard) => {
		if (!row.companyTransaction) return

		const transaction = {
			...row.companyTransaction,
			uid: String(row.companyTransaction.id)
		}

		openNestedUniversalDialog((onBack) => ({
			label: "Transaction Details",
			closeDialog: onBack ?? (() => S_UNIVERSAL_DIALOG.invoke(null)),
			sx: { maxWidth: "600px", width: "100%" },
			content: <TransactionDetails transaction={transaction} />
		}))
	}

	useEffect(() => {
		void loadData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedIssuerId, user?.uid])

	useEffect(() => {
		const loadPhone = async () => {
			if (!user) return
			const isOwnProfile = profile?.uid === user.uid
			if (isOwnProfile && (user.company_role === USER_ROLE.BUYER || company?.type === "private")) {
				setUserPhoneLoading(true)
				const res = await GD.REQ_KYC_PHONE_GET.request({ issuer: selectedIssuerId ?? undefined })
				if (res.data?.phone) {
					setUserPhone(`${res.data.area_code || ""} ${res.data.phone}`.trim())
				} else setUserPhone(null)
				setUserPhoneLoading(false)
			}
		}
		void loadPhone()
	}, [user?.uid, profile?.uid, company?.type, selectedIssuerId])

	useEffect(() => {
		if (activeTab === "transactions" && cards.length > 0) {
			void loadData()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab, cards.length, selectedIssuerId, user?.uid])

	const handleEdit = () => {
		openNestedUniversalDialog((onBack) => {
			const returnToUserDetails = onBack ?? (() => S_UNIVERSAL_DIALOG.invoke(null))
			return {
			label: "Edit user",
			content: (
				<>
					<FormIntro icon={Glyph.edit} title="Edit user" description="Update this person's details. Changes apply immediately." />
					<FormParser<{ user: IUserSaved }>
						key='editUser'
						onSubmit={async (data) => {
							const res = await GD.REQ_UPDATE_USER.request(data.user)
							if (res.error) return res
							if (res.data) {
								toast.success("User updated successfully")
								if (user?.team_uid !== data.user.team_uid)
									GD.REQ_TEAMS_LIST.request({ force: true })
								await onUpdate?.()
								returnToUserDetails()
							}
							return res
						}}
						onRequest={async () => {
							const res = await GD.REQ_GET_USER.request(userUid)
							return {
								user: {
									...res.data!,
									$__name_details: "",
									$__e_mail_details: "Used to sign in.",
									$__email_details: "Used to sign in.",
									$__password_details: "Only fill this field when updating the password.",
									$__team_uid_label: "Team",
									$__team_uid_details: "",
									$__company_role_hidden: true,
									$__team_uid_hidden: profile?.company_role !== USER_ROLE.ADMIN
								},
								$__user_decorator: { title: "Account details" }
							}
						}}
						primaryButton={{ title: "Save" }}
						secondaryButton={{
							title: "Cancel",
							onClick: returnToUserDetails
						}}
					/>
				</>
			),
			closeDialog: returnToUserDetails
		}}
		)
	}

	const returnToPreviousDialog = () => {
		if (!S_UNIVERSAL_DIALOG.back()) S_UNIVERSAL_DIALOG.invoke(null)
	}

	const handleTopUp = () => {
		const targetTeamUid = teamUid || user?.team_uid
		if (!targetTeamUid) return toast.error("Cannot top up: Team information missing.")
		S_UNIVERSAL_DIALOG.invoke({
			label: `Top Up — ${user?.name}`,
			content: (
				<FormParser
					key='teamUserTopUp'
					onSubmit={async (data) => {
						const parsedAmount = parsePositiveAmountToMinorUnits(data.amount)
						if (typeof parsedAmount.amount !== "number")
							return amountValidationError(parsedAmount.error || "Enter a valid amount.")
						const topUpToastId = `buyer-topup-${userUid}`
						toast.info(`Top up for ${user?.name || "buyer"} is being processed...`, {
							toastId: topUpToastId,
							autoClose: false,
							closeOnClick: false
						})
						const res = await GD.REQ_TEAM_USER_TOPUP.request({
							uid: targetTeamUid,
							user_uid: userUid,
							amount: parsedAmount.amount,
							currency: issuerCurrency,
							issuer: selectedIssuerId ?? undefined
						})
						if (res.error) {
							completeTrackedToast(
								topUpToastId,
								getFriendlyErrorMessage(res.error, "Top up failed."),
								"error"
							)
							return res
						}
						completeTrackedToast(
							topUpToastId,
							`Top up for ${user?.name || "buyer"} completed successfully.`,
							"success"
						)
						S_UNIVERSAL_DIALOG.invoke(null)
						const upd = await GD.REQ_GET_USER.request(userUid)
						if (upd.data) setUser(upd.data)
						onUpdate?.()
						return {}
					}}
					onRequest={async () => ({ ...(await GD.REQ_USER_TOPUP_TEMPLATE.request()).data!, $__issuer_hidden: true, $__issuer_label_hidden: true })}
					primaryButton={{ title: "Confirm Top Up" }}
					secondaryButton={{
						title: "Cancel",
						onClick: returnToPreviousDialog
					}}
				/>
			),
			closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
		})
	}

	const assignUserToTeam = async (
		targetTeamUid: string
	): Promise<{ error?: unknown }> => {
		if (!user) return { error: { code: 0, message: "User is not loaded yet." } }

		const res = await GD.REQ_TEAM_USER_ADD.request({
			uid: targetTeamUid,
			user_uid: user.uid,
			team_uid: targetTeamUid
		})

		if (res.error) {
			toast.error(getFriendlyErrorMessage(res.error, "Failed to assign user to team."))
			return res
		}

		toast.success("User assigned to team successfully")
		await GD.REQ_TEAMS_LIST.request({ force: true })
		await GD.REQ_USERS.request({ options: { include_teams: true } })
		const updatedUser = await GD.REQ_GET_USER.request(userUid)
		if (updatedUser.data) setUser(updatedUser.data)
		await onUpdate?.()
		return {}
	}

	const handleAssignToTeam = async () => {
		if (!user) return

		if (profile?.company_role === USER_ROLE.TEAM_LEAD) {
			const targetTeamUid = teamUid || profile?.team_uid
			if (!targetTeamUid) {
				toast.error(
					"Your team was not found. Open this user from a team page or contact an admin."
				)
				return
			}

			await assignUserToTeam(targetTeamUid)
			return
		}

		const teamsRes = await GD.REQ_TEAMS_LIST.request({ force: true })
		if (!teamsRes.data) {
			toast.error("Failed to load teams list")
			return
		}

		const availableTeams = teamsRes.data
			.filter((team) => team.status === "active")
			.map((team) => ({ name: team.name, value: team.uid }))

		if (availableTeams.length === 0) {
			toast.error("No active teams available for assignment")
			return
		}

		S_UNIVERSAL_DIALOG.invoke({
			label: `Assign ${user.name} to Team`,
			content: (
				<FormParser<{ team_uid: string }>
					key='assignUserToTeam'
					onSubmit={async (data) => {
						const result = await assignUserToTeam(data.team_uid)
						if (!result.error) {
							S_UNIVERSAL_DIALOG.invoke(null)
						}
						return result
					}}
					onRequest={async () => ({
						team_uid: "",
						$__team_uid_type: "select",
						$__team_uid_values: availableTeams,
						$__decorator: { title: "Assign to team" }
					})}
					primaryButton={{ title: "Assign" }}
					secondaryButton={{
						title: "Cancel",
						onClick: () => S_UNIVERSAL_DIALOG.invoke(null)
					}}
				/>
			),
			closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
		})
	}

	const refreshUserState = async () => {
		const targetUserUid = userUid
		const [userResponse, usersResponse, cardsResponse] = await Promise.all([
			GD.REQ_GET_USER.request(targetUserUid),
			GD.REQ_USERS.request({ options: { include_teams: true } }),
			GD.REQ_CARDS.request({ force: true })
		])
		const canUpdateLocalState =
			!userDetailsDisposed.current && activeUserUid.current === targetUserUid

		if (userResponse.data && canUpdateLocalState) setUser(userResponse.data)
		if (usersResponse.data) {
			GD.S_USERS_LIST_UPDATED.invoke(usersResponse.data)
		}
		if (cardsResponse.data) {
			GD.S_CARDS_LIST_UPDATED.invoke(cardsResponse.data)
			if (canUpdateLocalState) {
				setCards(cardsResponse.data.filter((card) => card.user_uid === targetUserUid))
				setCardsLoaded(true)
			}
		}

		await onUpdate?.()
	}

	const handleReturnBalance = () => {
		const targetTeamUid = teamUid || user?.team_uid
		const availableBalance = user?.team_balance
		if (availableBalance === undefined) {
			toast.warn(`${getIssuerDisplayName({ issuer: selectedIssuerId ?? 0, issuer_label: issuerContext?.selectedIssuer?.issuer_label, name: issuerContext?.selectedIssuer?.name })} buyer balance is unavailable. Refresh and try again.`)
			return
		}
		S_UNIVERSAL_DIALOG.invoke({
			label: `Return Balance — ${user?.name}`,
			content: (
				<ReturnBalanceForm
					key='teamUserWithdraw'
					availableBalance={availableBalance}
					currency={issuerCurrency}
					onCancel={returnToPreviousDialog}
					onSubmit={async (amount) => {
						const isBuyerReturningOwnBalance =
							profile?.company_role === USER_ROLE.BUYER && profile.uid === userUid
						const response = await GD.REQ_TEAM_USER_WITHDRAW.request({
							...(isBuyerReturningOwnBalance ? {} : { user_uid: userUid }),
							...(isBuyerReturningOwnBalance || !targetTeamUid ? {} : { uid: targetTeamUid }),
							amount,
							currency: issuerCurrency,
							issuer: selectedIssuerId ?? undefined
						})
						if (response.error) return response

						const remainingBalance = formatCurrencyWithNumberToSymbolWithNumber(
							response.data.balance,
							issuerCurrency
						)
						setUser(current =>
							current
								? withReturnedBuyerBalance(
									current,
									response.data.balance,
									selectedIssuerId,
									issuerCurrency
								)
								: current
						)
						if (isBuyerReturningOwnBalance) {
							GD.S_PROFILE_UPDATED.invoke(withReturnedBuyerBalance(
								{
									team_balance: profile?.team_balance,
									team_balances: profile?.team_balances
								},
								response.data.balance,
								selectedIssuerId,
								issuerCurrency
							))
						}
						toast.success(`Balance returned. Buyer balance: ${remainingBalance}.`)
						S_UNIVERSAL_DIALOG.invoke(null)
						const updatedUser = await GD.REQ_GET_USER.request(userUid)
						if (updatedUser.data) {
							setUser(withReturnedBuyerBalance(
								updatedUser.data,
								response.data.balance,
								selectedIssuerId,
								issuerCurrency
							))
						}
						await onUpdate?.()
						return {}
					}}
				/>
			),
			closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
		})
	}

	const startUserBlock = async (mode: "freeze" | "block") => {
		if (!user) return

		const targetUserUid = user.uid
		setUserActionPrompt(null)
		setUserActionBusy(true)
		setUserOperation({
			phase: "pending",
			title: "Starting user block",
			message:
				mode === "freeze"
					? "Sending the request to block login and freeze active cards..."
					: "Sending the request to block login, close cards and return card balances to the company..."
		})
		const response = await GD.REQ_USER_BLOCK.request({ user_uid: user.uid, mode })

		if (response.error || !response.data?.operation_uid) {
			const message = getFriendlyErrorMessage(response.error, "Failed to start user block.")
			if (!userDetailsDisposed.current && activeUserUid.current === targetUserUid) {
				setUserActionBusy(false)
				setUserOperation({ phase: "error", title: "User block was not started", message })
			}
			toast.error(message)
			return
		}

		const operationUid = response.data.operation_uid
		const toastId = `user-block-${operationUid}`
		const initialMessage =
			mode === "freeze"
				? "User login blocked. Freezing active cards..."
				: "User login blocked. Closing cards and returning card balances to the company..."
		toast.info(initialMessage, {
			toastId,
			autoClose: false,
			closeOnClick: false
		})

		void monitorUserBlockOperation(
			operationUid,
			(status) => {
				const progress = summarizeUserBlockProgress(status.cards)
				const progressMessage = getUserBlockProgressMessage(status.mode, progress)
				if (toast.isActive(toastId)) {
					toast.update(toastId, { render: progressMessage, type: "info", autoClose: false })
				}
				if (!userDetailsDisposed.current && activeUserUid.current === targetUserUid) {
					setUserOperation({
						phase: "pending",
						title: "User block in progress",
						message: progressMessage,
						progress
					})
				}
			},
			(error, statusErrorAttempt) => {
				const retryMessage = `Status check failed. Retrying (${statusErrorAttempt}/${USER_BLOCK_MAX_STATUS_ERRORS})...`
				if (toast.isActive(toastId)) {
					toast.update(toastId, { render: retryMessage, type: "warning", autoClose: false })
				}
				if (!userDetailsDisposed.current && activeUserUid.current === targetUserUid) {
					setUserOperation({
						phase: "pending",
						title: "Reconnecting to operation status",
						message: getFriendlyErrorMessage(error, retryMessage)
					})
				}
			}
		).then(async (result) => {
			const canUpdateLocalState =
				!userDetailsDisposed.current && activeUserUid.current === targetUserUid
			if (canUpdateLocalState) setUserActionBusy(false)

			if (result.kind === "completed") {
				const progress = summarizeUserBlockProgress(result.data.cards)
				const fundsWarning = result.data.error_message?.trim()
				const hasIssues = progress.failed > 0 || progress.skipped > 0 || Boolean(fundsWarning)
				const baseMessage =
					result.data.mode === "freeze"
						? "User blocked and cards frozen."
						: "User blocked and card close flow completed."
				const cardIssueMessage = progress.failed > 0 || progress.skipped > 0
					? ` Failed: ${progress.failed}, skipped: ${progress.skipped}.`
					: ""
				const message = `${baseMessage}${cardIssueMessage}${fundsWarning ? ` Warning: ${fundsWarning}` : ""}`

				if (canUpdateLocalState) {
					setUserOperation({
						phase: hasIssues ? "warning" : "success",
						title: hasIssues ? "Completed with warnings" : "User block completed",
						message,
						progress
					})
				}
				completeTrackedToast(toastId, message, hasIssues ? "warning" : "success")
				await refreshUserState()
				return
			}

			if (result.kind === "failed") {
				const failureDetails = getFriendlyErrorMessage(
					result.data.error_message,
					"One or more cards could not be processed."
				)
				const message = `User block failed. The user is active again. ${failureDetails}`
				const progress = summarizeUserBlockProgress(result.data.cards)
				if (canUpdateLocalState) {
					setUserOperation({
						phase: "error",
						title: "User block failed",
						message,
						progress
					})
				}
				completeTrackedToast(toastId, message, "error")
				await refreshUserState()
				return
			}

			const message =
				result.kind === "timeout"
					? "The operation may still be running, but its final status was not confirmed within two minutes."
					: getFriendlyErrorMessage(
							result.error,
							"The operation may still be running, but its status is temporarily unavailable."
						)
			if (canUpdateLocalState) {
				setUserOperation({ phase: "warning", title: "Final status is unknown", message })
			}
			completeTrackedToast(toastId, message, "warning")
			await refreshUserState()
		})
	}

	const submitCardIssueLimit = async (data: CardIssueLimitFormData) => {
		if (!user) return { error: { code: 0, message: "User is not loaded yet." } }

		const response = await GD.REQ_USER_CARD_ISSUE_LIMIT.request({
			uid: user.uid,
			card_issue_limit: Number(data.card_issue_limit) || 0,
			card_issue_limit_status: data.card_issue_limit_status
		})
		if (response.error) {
			toast.error(
				getFriendlyErrorMessage(response.error, "Failed to update card issue limit.")
			)
			return response
		}

		toast.success("Card issue limit updated.")
		S_UNIVERSAL_DIALOG.invoke(null)
		const refreshed = await GD.REQ_GET_USER.request(user.uid)
		if (refreshed.data) setUser(refreshed.data)
		return {}
	}

	const handleBlockUser = () => {
		if (!user) return
		setReturnPreviewLoading(true)
		setUserActionPrompt("block")
	}

	const unblockUser = async (unfreezeCards: boolean) => {
		if (!user) return

		const targetUserUid = user.uid
		const toastId = `user-unblock-${targetUserUid}`
		setUserActionPrompt(null)
		setUserActionBusy(true)
		setUserOperation({
			phase: "pending",
			title: "Restoring user",
			message: unfreezeCards
				? "Restoring login and unfreezing cards..."
				: "Restoring user login..."
		})
		toast.info(unfreezeCards ? "Restoring login and cards..." : "Restoring user login...", {
			toastId,
			autoClose: false,
			closeOnClick: false
		})
		const response = await GD.REQ_USER_UNBLOCK.request({
			user_uid: user.uid,
			unfreeze_cards: unfreezeCards ? "true" : "false"
		})
		const canUpdateLocalState =
			!userDetailsDisposed.current && activeUserUid.current === targetUserUid
		if (canUpdateLocalState) setUserActionBusy(false)

		if (response.error) {
			const message = getFriendlyErrorMessage(response.error, "Failed to unblock user.")
			if (canUpdateLocalState) {
				setUserOperation({ phase: "error", title: "User restore failed", message })
			}
			completeTrackedToast(toastId, message, "error")
			return
		}

		await refreshUserState()
		const failedCount = response.data.failed_card_uids?.length ?? 0
		const message = failedCount
			? `User restored. ${failedCount} cards failed to unfreeze.`
			: "User restored successfully."
		if (canUpdateLocalState) {
			setUserOperation({
				phase: failedCount ? "warning" : "success",
				title: failedCount ? "User restored with card issues" : "User restored",
				message
			})
		}
		completeTrackedToast(toastId, message, failedCount ? "warning" : "success")
	}

	const handleUnblockUser = () => {
		if (!user) return
		setUserActionPrompt("unblock")
	}

	const syncCurrentUser = async (nextMethod: TTwoFactorMethod | null) => {
		setTwoFactorMethodOverride(nextMethod)
		setUser((prev) => (prev ? { ...prev, twofa_method: nextMethod } : prev))
		const upd = await GD.REQ_GET_USER.request(userUid)
		if (upd.data) setUser({ ...upd.data, twofa_method: nextMethod })
		await onUpdate?.()
	}

	const handleStartTwoFactorSetup = async () => {
		setTwoFactorError(null)
		setTwoFactorBusy(true)
		const response = await GD.REQ_AUTH_2FA_ENABLE.request({ method: "totp" })
		setTwoFactorBusy(false)
		if (response.error)
			return setTwoFactorError(response.error.message || "Failed to start 2FA setup")
		if (!response.data?.url) return setTwoFactorError("Activation link not returned")
		setShowDisableTwoFactorForm(false)
		setDisableTwoFactorPassword("")
		setTwoFactorCode("")
		try {
			setTwoFactorQrCodeDataUrl(
				await QRCode.toDataURL(response.data.url, { width: 200, margin: 1 })
			)
		} catch {
			setTwoFactorQrCodeDataUrl("")
		}
		setTwoFactorSetupUrl(response.data.url)
	}

	const handleConfirmTwoFactor = async () => {
		const normalizedCode = twoFactorCode.trim()
		if (normalizedCode.length < TWO_FACTOR_CODE_LENGTH) {
			setTwoFactorError(
				`Enter the ${TWO_FACTOR_CODE_LENGTH}-digit code from your authenticator app`
			)
			return
		}
		setTwoFactorError(null)
		setTwoFactorBusy(true)
		const response = await GD.REQ_AUTH_2FA_VALIDATE.request({
			totp: normalizedCode,
			method: "totp"
		})
		setTwoFactorBusy(false)
		if (response) return setTwoFactorError(response.message || "Failed to activate 2FA")
		await syncCurrentUser("totp")
		setTwoFactorSetupUrl("")
		setTwoFactorQrCodeDataUrl("")
		setTwoFactorCode("")
		toast.success("Two-factor authentication activated.")
	}

	const handleOpenAuthenticatorSetup = async () => {
		setTwoFactorError(null)
		if (await openGoogleAuthenticator(twoFactorSetupUrl)) return
		setTwoFactorError("Google Authenticator is not installed or could not be opened")
	}

	const handleDisableTwoFactor = async () => {
		if (!disableTwoFactorPassword.trim()) return setTwoFactorError("Password is required")
		setTwoFactorError(null)
		setTwoFactorBusy(true)
		const response = await GD.REQ_AUTH_2FA_DISABLE.request({
			password: disableTwoFactorPassword
		})
		setTwoFactorBusy(false)
		if (response) return setTwoFactorError(response.message || "Failed to disable 2FA")
		await syncCurrentUser(null)
		setTwoFactorSetupUrl("")
		setTwoFactorQrCodeDataUrl("")
		setTwoFactorCode("")
		setDisableTwoFactorPassword("")
		setShowDisableTwoFactorForm(false)
		toast.success("Two-factor authentication disabled.")
	}

	const handleCloseTwoFactorModal = () => {
		setTwoFactorSetupUrl("")
		setTwoFactorQrCodeDataUrl("")
		setTwoFactorCode("")
		setTwoFactorError(null)
	}

	if (!user) {
		return (
			<VBox
				role='status'
				gap='12px'
				sx={{
					alignItems: "center",
					justifyContent: "center",
					height: "400px",
					width: "100%",
					background: DS.surface
				}}
			>
				<Spinner color={DS.accent} />
				<Typo sx={{ color: DS.muted, fontSize: "13px" }}>Loading profile…</Typo>
			</VBox>
		)
	}

	const isOwnProfile = profile?.uid === user.uid
	const twoFactorMethod =
		twoFactorMethodOverride !== undefined
			? twoFactorMethodOverride
			: (user.twofa_method ?? (isOwnProfile ? profile?.twofa_method : undefined) ?? null)
	const isTwoFactorEnabled = !!twoFactorMethod
	const canManageUser =
		!isOwnProfile &&
		user.company_role !== USER_ROLE.ADMIN &&
		((profile?.company_role === USER_ROLE.ADMIN &&
			(user.company_role === USER_ROLE.BUYER ||
				user.company_role === USER_ROLE.TEAM_LEAD)) ||
			(profile?.company_role === USER_ROLE.TEAM_LEAD &&
				user.company_role === USER_ROLE.BUYER))
	const canAssignUserToTeam = canManageUser && !user.team_uid
	const isUserDisabled = user.company_status === "disabled"
	const canReturnBalance =
		user.company_role === USER_ROLE.BUYER &&
		(profile?.company_role === USER_ROLE.ADMIN ||
			profile?.company_role === USER_ROLE.TEAM_LEAD ||
			(profile?.company_role === USER_ROLE.BUYER && profile.uid === user.uid))

	const joinedDate = new Date(user.created_at).toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric"
	})
	// users/get is issuer-scoped by the active request context. The scalar is the
	// authoritative value for that response; the per-issuer array is optional.
	const currentTeamBalance = user.team_balance
	const formattedTeamBalance =
		currentTeamBalance === undefined
			? "—"
			: formatCurrencyWithNumberToSymbolWithNumber(currentTeamBalance, issuerCurrency)
	const usesIssuerScopedCardMetrics = selectedIssuerId !== null
	const formattedCardsBalance = usesIssuerScopedCardMetrics
		? cardsLoaded
			? formatCurrencyWithNumberToSymbolWithNumber(actualCardsBalance, issuerCurrency)
			: "—"
		: user.cards_balance !== undefined
			? formatCurrencyWithNumberToSymbolWithNumber(user.cards_balance, issuerCurrency)
			: formatCurrencyWithNumberToSymbolWithNumber(actualCardsBalance, issuerCurrency)
	const cardsIssued = usesIssuerScopedCardMetrics
		? cardsLoaded ? buyerCardSummary.total : "—"
		: user.cards_count ?? buyerCardSummary.total
	const cardIssueLimit = user.card_issue_limit !== undefined ? user.card_issue_limit : "—"
	const cardIssueLimitText = `${cardIssueLimit}${
		user.card_issue_limit_status ? ` (${user.card_issue_limit_status})` : ""
	}`
	const canManageTeamMember = profile?.company_role === USER_ROLE.ADMIN || profile?.company_role === USER_ROLE.TEAM_LEAD
	const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" } as const
	const openCardIssueLimit = () =>
		openNestedUniversalDialog(onBack => ({
			label: "Set Card Issue Limit",
			content: (
				<>
					<FormIntro icon={Glyph.limit} title="Card issue limit" description="Cap how many cards this buyer can issue. Turn it off for no limit." />
					<FormParser
						key='cardIssueLimit'
						onSubmit={submitCardIssueLimit}
						onRequest={async () => {
							const template =
								await GD.REQ_USER_CARD_ISSUE_LIMIT_TEMPLATE.request()
							return {
								...(template.data || {
									uid: user.uid,
									card_issue_limit: user.card_issue_limit ?? 0,
									card_issue_limit_status:
										user.card_issue_limit_status ?? "disabled"
								}),
								$__uid_hidden: true,
								$__decorator: { title: "Card Issue Limit" }
							}
						}}
						primaryButton={{ title: "Save" }}
						secondaryButton={{
							title: "Cancel",
						onClick: onBack ?? (() => S_UNIVERSAL_DIALOG.invoke(null))
						}}
					/>
				</>
			),
			closeDialog: onBack ?? (() => S_UNIVERSAL_DIALOG.invoke(null))
		}))

	const userDetailsTabs = (
		user.company_role === USER_ROLE.BUYER
			? ["overview", "cards", "transactions"]
			: ["overview"]
	) as Array<"overview" | "cards" | "transactions">

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: 0,
				width: "100%",
				backgroundColor: USER_DETAILS_SOFT
			}}
		>
			<Box
				sx={{
					position: "relative",
					backgroundColor: "#FFFFFF",
					borderBottom: `1px solid ${USER_DETAILS_BORDER}`,
					padding: "22px 24px 20px",
					"@media (max-width: 600px)": { padding: "14px 12px 12px" },
					flexShrink: 0
				}}
			>
				<Box
					element='button'
					type='button'
					aria-label='Close user details'
					onClick={onClose}
					sx={{
						position: "absolute",
						top: "18px",
						right: "24px",
						width: "34px",
						height: "34px",
						padding: 0,
						borderRadius: "8px",
						cursor: "pointer",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: "#F4F7F9",
						border: "1px solid transparent",
						color: USER_DETAILS_MUTED,
						transition: "all 0.15s",
						zIndex: 1,
						"@media (max-width: 600px)": { top: "14px", right: "12px" },
						"&:hover": {
							backgroundColor: "#EAF0F4",
							borderColor: USER_DETAILS_BORDER,
							color: USER_DETAILS_TEXT
						}
					}}
				>
					<svg
						width='14'
						height='14'
						viewBox='0 0 24 24'
						fill='none'
						stroke='currentColor'
						strokeWidth='2.5'
						strokeLinecap='round'
						strokeLinejoin='round'
						aria-hidden='true'
					>
						<line x1='18' y1='6' x2='6' y2='18' />
						<line x1='6' y1='6' x2='18' y2='18' />
					</svg>
				</Box>
				<EntityHeader
					leading={<Avatar name={user.name} size={52} />}
					title={user.name}
					badge={<StatusBadge status={user.company_status} />}
					meta={
						<>
							{[String(user.company_role ?? "").replace(/^./, char => char.toUpperCase()), user.team_name, `Joined ${joinedDate}`].filter(Boolean).join(" · ")}
							{user.email && <><br /><BreakableEmail email={user.email} /></>}
						</>
					}
				/>
			</Box>

			<Box
				sx={{
					flex: 1,
					overflowY: "auto",
					padding: "20px 24px 24px",
					"@media (max-width: 600px)": { padding: "14px 10px 18px" },
					display: "flex",
					flexDirection: "column",
					gap: "14px"
				}}
			>
				{userOperation && (
					<UserOperationNotice
						operation={userOperation}
						onDismiss={() => setUserOperation(null)}
						onRefresh={() => {
							void refreshUserState()
						}}
					/>
				)}

				{userActionPrompt && (
					<UserActionPromptPanel
						kind={userActionPrompt}
						busy={userActionBusy}
						returnPreview={<UserBlockReturnPreview userUid={user.uid} onLoadingChange={setReturnPreviewLoading} />}
						returnPreviewLoading={returnPreviewLoading}
						frozenCardCount={buyerCardSummary.frozen}
						onFreeze={() => {
							void startUserBlock("freeze")
						}}
						onBlock={() => {
							void startUserBlock("block")
						}}
						onRestoreLogin={() => {
							void unblockUser(false)
						}}
						onRestoreCards={() => {
							void unblockUser(true)
						}}
						onCancel={() => setUserActionPrompt(null)}
					/>
				)}

				{userDetailsTabs.length > 1 && (
					<UnderlineTabs tabs={userDetailsTabs} active={activeTab} onChange={setActiveTab} label='User detail sections' />
				)}

				{activeTab === "overview" && (
					<VBox gap='14px' sx={{ width: "100%" }}>
					{user.company_role === USER_ROLE.BUYER && (
						<Stage>
							<VBox gap='18px' sx={{ minWidth: 0, padding: "4px 4px 4px 8px", [DS.mobile]: { padding: 0 } }}>
								<VBox gap='6px'>
									<Typo sx={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: DS.muted }}>Balance</Typo>
									<Typo sx={{ fontSize: "44px", lineHeight: 1.05, fontWeight: 700, letterSpacing: "-0.035em", color: DS.ink, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere", [DS.mobile]: { fontSize: "36px" } }}>{formattedTeamBalance}</Typo>
									<Typo sx={{ fontSize: "13px", color: DS.muted }}>Funds this buyer can load onto cards</Typo>
								</VBox>
								{(profile?.company_role !== USER_ROLE.BUYER || (canReturnBalance && currentTeamBalance !== undefined)) && (
									<HBox gap='10px' sx={{ flexWrap: "wrap" }}>
										{profile?.company_role !== USER_ROLE.BUYER && (
											<StageButton onClick={handleTopUp}>
												<svg aria-hidden width='16' height='16' viewBox='0 0 24 24' {...stroke}><path d='M12 5v14' /><path d='M5 12h14' /></svg>
												Top Up
											</StageButton>
										)}
										{canReturnBalance && currentTeamBalance !== undefined && (
											<StageButton ghost onClick={handleReturnBalance}>
												<svg aria-hidden width='16' height='16' viewBox='0 0 24 24' {...stroke}><path d='M9 14 4 9l5-5' /><path d='M4 9h11a5 5 0 0 1 0 10h-1' /></svg>
												Return
											</StageButton>
										)}
									</HBox>
								)}
							</VBox>
							<Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
								<StageStat label='On cards'>{formattedCardsBalance ?? "—"}</StageStat>
								<StageStat label='Cards issued'>{cardsIssued}</StageStat>
								<StageStat label='Issue limit'>{cardIssueLimitText}</StageStat>
								<StageStat label='Unavailable cards'>
									{buyerCardSummary.unavailable}
									<Box element='span' sx={{ display: "block", marginTop: "2px", fontSize: "11px", fontWeight: 500, color: DS.muted, whiteSpace: "normal" }}>
										{`${buyerCardSummary.blocked} blocked · ${buyerCardSummary.frozen} frozen · ${buyerCardSummary.closed} closed`}
									</Box>
								</StageStat>
							</Box>
						</Stage>
					)}

					{canManageTeamMember && (
						<QuickActions label='User actions'>
							<QuickAction title='Edit' onClick={handleEdit} icon={<svg viewBox='0 0 24 24' {...stroke}><path d='M12 20h9' /><path d='M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z' /></svg>} />
							{user.company_role === USER_ROLE.BUYER && (
								<QuickAction title='Card limit' onClick={openCardIssueLimit} icon={<svg viewBox='0 0 24 24' {...stroke}><rect x='2' y='5' width='20' height='14' rx='2' /><path d='M2 10h20' /><path d='M6 15h4' /></svg>} />
							)}
							{canAssignUserToTeam && (
								<QuickAction title='Assign Team' onClick={handleAssignToTeam} icon={<svg viewBox='0 0 24 24' {...stroke}><path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' /><circle cx='9' cy='7' r='4' /><path d='M19 8v6M22 11h-6' /></svg>} />
							)}
						</QuickActions>
					)}

					<Section title='Details'>
						<Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", columnGap: "32px", [DS.mobile]: { gridTemplateColumns: "minmax(0, 1fr)" } }}>
							<DetailRow label='Team' value={user.team_name || "No team"} />
							<DetailRow label='Login status' value={<StatusBadge status={user.status} />} />
							<DetailRow label='Company access' value={<StatusBadge status={user.company_status} />} />
							{user.status !== user.company_status && (
								<Typo sx={{ color: DS.muted, fontSize: "12px", lineHeight: 1.45, padding: "0 0 10px", gridColumn: "1 / -1" }}>
									Login status controls the account identity; company access controls entry to this workspace.
								</Typo>
							)}
							<DetailRow label='User UID' value={user.uid} mono copy={user.uid} />
							<DetailRow label='Company UID' value={user.company_uid} mono copy={user.company_uid} />
						</Box>
					</Section>
					{canManageUser && (
						<Section title='Access'>
							<Box sx={{ padding: "4px 0 12px" }}>
								<Box sx={{ borderRadius: "14px", border: `1px solid ${DS.line}`, overflow: "hidden" }}>
									<SettingRow
										tone={isUserDisabled ? "success" : "danger"}
										title={isUserDisabled ? "Access is blocked" : "Block user"}
										description={isUserDisabled ? "This user cannot sign in to the workspace. Restore to let them back in." : "Stops sign-in to this workspace. You choose what happens to their cards next."}
										icon={isUserDisabled
											? <svg width='18' height='18' viewBox='0 0 24 24' {...stroke}><rect x='3' y='11' width='18' height='11' rx='2' /><path d='M7 11V7a5 5 0 0 1 9.9-1' /></svg>
											: <svg width='18' height='18' viewBox='0 0 24 24' {...stroke}><circle cx='12' cy='12' r='9' /><path d='M5.6 5.6l12.8 12.8' /></svg>}
									>
										<ControlButton
											tone={isUserDisabled ? "success" : "danger"}
											disabled={userActionBusy}
											onClick={isUserDisabled ? handleUnblockUser : handleBlockUser}
										>
											{isUserDisabled ? (userActionBusy ? "Restoring..." : "Restore access") : (userActionBusy ? "Working..." : "Block")}
										</ControlButton>
									</SettingRow>
								</Box>
							</Box>
						</Section>
					)}
					{isOwnProfile && (
						<Section title='Your account'>

						{isOwnProfile && (user.company_role === USER_ROLE.BUYER || company?.type === "private") && (
							<FieldRow
								label='Phone Number'
								value={
									<HBox gap='10px' sx={{ alignItems: "center" }}>
										<Typo variant='medium' sx={{ fontWeight: 500, color: USER_DETAILS_TEXT, fontSize: "0.875rem" }}>
											{userPhoneLoading ? "Loading..." : (userPhone || "Not set")}
										</Typo>
										<Button
											title={userPhone ? "Edit" : "Set Phone"}
											variant='secondary'
											disabled={userPhoneLoading}
											onClick={async () => {
												const { openKycPhoneForm } = await import("./KycPhoneForm")
												openKycPhoneForm(selectedIssuerId ?? undefined, async () => {
													setUserPhoneLoading(true)
													const res = await GD.REQ_KYC_PHONE_GET.request({ issuer: selectedIssuerId ?? undefined })
													if (res.data?.phone) {
														setUserPhone(`${res.data.area_code || ""} ${res.data.phone}`.trim())
													} else setUserPhone(null)
													setUserPhoneLoading(false)
												})
											}}
											sx={{ padding: "4px 12px", fontSize: "0.8rem" }}
										/>
									</HBox>
								}
							/>
						)}
						{isOwnProfile && (
							<VBox
								gap='10px'
								sx={{ paddingTop: "12px", borderTop: `1px solid ${DS.line}` }}
							>
								<SectionLabel>Security</SectionLabel>
								<HBox
									sx={{
										alignItems: "center",
										justifyContent: "space-between",
										padding: "10px 0",
										borderBottom: "1px solid #F3F4F6"
									}}
								>
									<Typo variant='caption' sx={{ color: USER_DETAILS_MUTED, fontSize: "0.875rem" }}>
										2FA
									</Typo>
									<HBox gap='10px' sx={{ alignItems: "center" }}>
										<Typo
											variant='medium'
											sx={{ fontWeight: 500, color: USER_DETAILS_TEXT, fontSize: "0.875rem" }}
										>
											{isTwoFactorEnabled ? "Enabled" : "Disabled"}
										</Typo>
										<Button
											title={isTwoFactorEnabled ? "Disable" : "Enable"}
											variant='secondary'
											disabled={twoFactorBusy}
											onClick={() => {
												setTwoFactorError(null)
												if (isTwoFactorEnabled) {
													setShowDisableTwoFactorForm(true)
													return
												}
												handleStartTwoFactorSetup()
											}}
											sx={{
												padding: "4px 12px",
												fontSize: "0.8rem",
												color: isTwoFactorEnabled ? "#DC2626" : undefined,
												borderColor: isTwoFactorEnabled ? "#FCA5A5" : undefined
											}}
										/>
									</HBox>
								</HBox>

								{twoFactorError && (
									<Box
										sx={{
											backgroundColor: "#FEF2F2",
											border: "1px solid #FCA5A5",
											borderRadius: "6px",
											padding: "8px 12px"
										}}
									>
										<Typo sx={{ color: "#B91C1C", fontSize: "0.85rem" }}>
											{twoFactorError}
										</Typo>
									</Box>
								)}

								{isTwoFactorEnabled && showDisableTwoFactorForm && (
									<VBox
										gap='10px'
										sx={{
											backgroundColor: USER_DETAILS_SOFT,
											border: `1px solid ${USER_DETAILS_BORDER}`,
											borderRadius: "8px",
											padding: "14px"
										}}
									>
										<Typo
											sx={{ color: USER_DETAILS_MUTED, fontSize: "0.875rem", lineHeight: 1.5 }}
										>
											Enter your current password to disable 2FA.
										</Typo>
										<Input
											data={{ password: disableTwoFactorPassword }}
											name='password'
											type='password'
											onChange={(v) => setDisableTwoFactorPassword(v)}
											placeholder='Enter current password'
										/>
										<HBox gap='8px'>
											<Button
												title={twoFactorBusy ? "..." : "Confirm"}
												variant='primary'
												disabled={twoFactorBusy}
												onClick={handleDisableTwoFactor}
												sx={{ flex: 1 }}
											/>
											<Button
												title='Cancel'
												variant='secondary'
												disabled={twoFactorBusy}
												onClick={() => {
													setShowDisableTwoFactorForm(false)
													setDisableTwoFactorPassword("")
													setTwoFactorError(null)
												}}
											/>
										</HBox>
									</VBox>
								)}
							</VBox>
						)}

						</Section>
					)}

					{isOwnProfile &&
						(() => {
							// Private-company account holders are ADMINs, not BUYERs, so the
							// original buyer-only gate hid KYC from them entirely. Show it for
							// them too, but drop it once they're verified (they normally clear
							// KYC through the full-screen OnboardingGate before reaching here).
							const isPrivate = company?.type === "private"
							if (user.company_role !== USER_ROLE.BUYER && !isPrivate) return null
							return <KycSection hideWhenApproved={isPrivate} />
						})()}
					</VBox>
				)}

				{USER_ROLE.BUYER === user.company_role && activeTab === "cards" && (
					<Box sx={{ flexShrink: 0 }}>
						<Box sx={{ height: buyerCardSummary.total === 0 ? "300px" : "560px", "@media (max-width: 600px)": { height: "auto", minHeight: buyerCardSummary.total === 0 ? "180px" : "auto" } }}>
							<CardsList
								view="grid"
								onSelect={(card) => {
									openNestedUniversalDialog((onBack) => ({
										label: "Card Details",
										hideHistoryBack: true,
										closeDialog: onBack ?? (() => S_UNIVERSAL_DIALOG.invoke(null)),
										sx: { maxWidth: "850px", width: "100%" },
										content: (
											<Box sx={{ maxHeight: "80vh", overflowY: "auto" }}>
												<CardDetails
													card={card}
													onClose={() => S_UNIVERSAL_DIALOG.invoke(null)}
													onBack={onBack}
												/>
											</Box>
										)
									}))
								}}
								userUid={user.uid}
								dependency={0}
								filter={{
									order_id: "",
									code: "",
									status: "",
									currency: "",
									country: ""
								}}
							/>
						</Box>
					</Box>
				)}

				{USER_ROLE.BUYER === user.company_role && activeTab === "transactions" && (
					<Box
						sx={{
							flexShrink: 0,
							backgroundColor: DS.surface,
							border: `1px solid ${DS.line}`,
							borderRadius: DS.radius,
							overflow: "hidden",
							boxShadow: DS.shadow
						}}
					>
						<Box
							sx={{
								height: transactions.length === 0 ? "300px" : "560px",
								overflow: "auto",
								"@media (max-width: 600px)": {
									height: transactions.length === 0 ? "240px" : "auto",
									maxHeight: "min(560px, 65dvh)"
								}
							}}
						>
							{isLoadingTransactions ? (
								<VBox
									sx={{
										alignItems: "center",
										justifyContent: "center",
										height: "100%"
									}}
								>
									<Spinner color={DS.accent} />
								</VBox>
							) : transactions.length > 0 ? (
								<VBox gap='0' sx={{ padding: "4px 16px" }}>
									{transactions.map((row, index) => {
										const direction = row.companyTransaction?.direction ?? row.direction
										const income = direction === "income"
										const expense = direction === "expense"
										const meta = [
											new Date(row.date).toLocaleString("en-GB", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }),
											row.card && `${row.card.name || "Card"} •••• ${(row.card.mask || "").slice(-4)}`
										].filter(Boolean).join(" · ")
										return (
											<HBox
												key={`${row.date}-${index}`}
												role='button'
												tabIndex={0}
												onClick={() => openTransactionDetails(row)}
												onKeyDown={(event: { key: string }) => {
													if (event.key === "Enter" || event.key === " ") openTransactionDetails(row)
												}}
												gap='12px'
												sx={{
													alignItems: "center",
													padding: "12px 8px",
													margin: "0 -8px",
													borderRadius: "12px",
													borderTop: `1px solid ${DS.line}`,
													cursor: "pointer",
													"&:first-of-type": { borderTop: 0 },
													"&:hover": { background: DS.soft },
													"&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "-2px" }
												}}
											>
												<Box
													aria-hidden
													sx={{
														width: "38px",
														height: "38px",
														flex: "0 0 38px",
														borderRadius: "12px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														background: income ? "#ECFDF5" : expense ? "#FEF2F2" : DS.accentSoft,
														color: income ? "#047857" : expense ? "#B42318" : DS.accent
													}}
												>
													<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>
														{income ? <path d='M17 7 7 17M7 17h8M7 17V9' /> : expense ? <path d='M7 17 17 7M17 7H9M17 7v8' /> : <path d='M7 7h10v10H7z' />}
													</svg>
												</Box>
												<VBox gap='3px' sx={{ minWidth: 0, flex: 1 }}>
													<Typo sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere" }}>
														{row.description || "Transaction"}
													</Typo>
													<Typo sx={{ fontSize: "12px", color: DS.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{meta}</Typo>
												</VBox>
												<VBox gap='5px' sx={{ alignItems: "flex-end", flexShrink: 0 }}>
													<Typo sx={{ fontSize: "14px", fontWeight: 700, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: income ? "#047857" : expense ? "#B42318" : DS.ink }}>
														{income ? "+" : expense ? "−" : ""}
														{formatCurrencyWithNumberToSymbolWithNumber(Math.abs(row.amount), row.currency)}
													</Typo>
													<StatusBadge status={row.status} />
												</VBox>
											</HBox>
										)
									})}
								</VBox>
							) : (
								<VBox
									sx={{
										alignItems: "center",
										justifyContent: "center",
										height: "100%",
										color: "#9CA3AF"
									}}
								>
									<Typo variant='medium' sx={{ color: "#9CA3AF", fontSize: "0.9rem" }}>
										No transactions found
									</Typo>
								</VBox>
							)}
						</Box>
					</Box>
				)}
			</Box>

			{twoFactorSetupUrl && (
				<Box
					sx={{
						position: "fixed",
						inset: 0,
						backgroundColor: "rgba(17, 24, 39, 0.45)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "24px",
						zIndex: 100
					}}
					onClick={() => {
						if (!twoFactorBusy) handleCloseTwoFactorModal()
					}}
				>
					<Box
						sx={{
							width: "100%",
							maxWidth: "440px",
							backgroundColor: "#FFFFFF",
							borderRadius: "12px",
							border: "1px solid #E5E7EB",
							boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
							padding: "28px"
						}}
						onClick={(e) => e?.stopPropagation()}
					>
						<HBox
							sx={{
								justifyContent: "space-between",
								alignItems: "flex-start",
								marginBottom: "20px"
							}}
						>
							<VBox gap='4px'>
								<Typo
									variant='medium'
									sx={{ fontWeight: 700, color: USER_DETAILS_TEXT, fontSize: "1.05rem" }}
								>
									Set Up Two-Factor Auth
								</Typo>
								<Typo sx={{ color: USER_DETAILS_MUTED, fontSize: "0.875rem" }}>
									Scan QR code, then confirm with a code from your app.
								</Typo>
							</VBox>
							<Box
								sx={{
									cursor: "pointer",
									color: "#9CA3AF",
									"&:hover": { color: USER_DETAILS_TEXT },
									marginLeft: "16px"
								}}
								onClick={handleCloseTwoFactorModal}
							>
								<svg
									width='20'
									height='20'
									viewBox='0 0 24 24'
									fill='none'
									stroke='currentColor'
									strokeWidth='2'
									strokeLinecap='round'
									strokeLinejoin='round'
								>
									<line x1='18' y1='6' x2='6' y2='18' />
									<line x1='6' y1='6' x2='18' y2='18' />
								</svg>
							</Box>
						</HBox>
						<VBox gap='20px'>
							{nativeApp && (
								<Button
									title='Open Google Authenticator'
									variant='primary'
									disabled={twoFactorBusy}
									onClick={handleOpenAuthenticatorSetup}
									sx={{ width: "100%", padding: "10px" }}
								/>
							)}
							{twoFactorQrCodeDataUrl && (
								<VBox
									sx={{
										alignItems: "center",
										padding: "20px",
										backgroundColor: USER_DETAILS_SOFT,
										borderRadius: "8px",
										border: "1px solid #E5E7EB"
									}}
								>
									<img
										src={twoFactorQrCodeDataUrl}
										alt='QR Code'
										style={{ display: "block", width: "160px", height: "160px" }}
									/>
									<Typo
										sx={{
											color: USER_DETAILS_MUTED,
											fontSize: "0.8rem",
											textAlign: "center",
											marginTop: "10px"
										}}
									>
										Use Google Authenticator, Authy, or 1Password
									</Typo>
								</VBox>
							)}
							<VBox gap='6px'>
								<Typo sx={{ color: USER_DETAILS_TEXT, fontWeight: 600, fontSize: "0.875rem" }}>
									Or copy the manual link
								</Typo>
								<HBox
									sx={{
										backgroundColor: USER_DETAILS_SOFT,
										border: `1px solid ${USER_DETAILS_BORDER}`,
										borderRadius: "6px",
										padding: "8px 12px",
										alignItems: "center",
										gap: "10px"
									}}
								>
									<Typo
										sx={{
											fontFamily: "monospace",
											color: USER_DETAILS_MUTED,
											fontSize: "0.8rem",
											flex: 1,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap"
										}}
									>
										{twoFactorSetupUrl}
									</Typo>
									<Button
										title='Copy'
										variant='secondary'
										onClick={() => {
											navigator.clipboard.writeText(twoFactorSetupUrl)
											toast.success("Copied")
										}}
										sx={{ padding: "3px 10px", fontSize: "0.8rem" }}
									/>
								</HBox>
							</VBox>
							{twoFactorError && (
								<Box
									sx={{
										backgroundColor: "#FEF2F2",
										border: "1px solid #FCA5A5",
										borderRadius: "6px",
										padding: "10px 14px"
									}}
								>
									<Typo sx={{ color: "#B91C1C", fontSize: "0.875rem" }}>
										{twoFactorError}
									</Typo>
								</Box>
							)}
							<Input
								data={{ totp: twoFactorCode }}
								name='totp'
								onChange={(v) => setTwoFactorCode(v)}
								placeholder='6-digit code'
								sx={{
									textAlign: "center",
									letterSpacing: "0.25em",
									fontSize: "1.1rem",
									padding: "10px"
								}}
							/>
							<Button
								title={twoFactorBusy ? "Activating..." : "Confirm"}
								variant='primary'
								disabled={twoFactorBusy}
								onClick={handleConfirmTwoFactor}
								sx={{ width: "100%", padding: "10px" }}
							/>
						</VBox>
					</Box>
				</Box>
			)}
		</Box>
	)
}
